/**
 * ContactActionButtons - Reusable action row for contacts and places
 * 
 * Displays Call, Email, Directions buttons based on available data.
 * Includes copy functionality for each field.
 * 
 * Features:
 * - Only shows actions when data is available
 * - Accessible labels and 44pt touch targets
 * - Copy functionality for phone, email, address
 * - Graceful fallbacks for missing apps
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Phone, Mail, MapPin, Copy, ChevronDown, ExternalLink, Loader2, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import {
  openPhoneDialer,
  openEmailComposer,
  openMapsWithDirections,
  copyToClipboard,
  isValidPhone,
  isValidEmail,
  formatPhoneForDialing,
  formatPhoneForDisplay,
  buildAddressString,
} from '@/services/nativeIntegrationsService';

/**
 * Compact action button row for contact/place details
 */
export const ContactActionRow = ({
  phone,
  email,
  address,
  businessName,
  extension,
  className = '',
}) => {
  const [loading, setLoading] = useState(null);

  const hasPhone = phone && isValidPhone(phone);
  const hasEmail = email && isValidEmail(email);
  const hasAddress = address || businessName;

  // Don't render if no actions available
  if (!hasPhone && !hasEmail && !hasAddress) {
    return null;
  }

  const handleCall = async () => {
    setLoading('call');
    await openPhoneDialer(phone, extension);
    setLoading(null);
  };

  const handleEmail = async () => {
    setLoading('email');
    await openEmailComposer(email);
    setLoading(null);
  };

  const handleDirections = async () => {
    setLoading('directions');
    await openMapsWithDirections(address, businessName);
    setLoading(null);
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="contact-action-row">
      {hasPhone && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleCall}
          disabled={loading === 'call'}
          className="h-11 px-4 gap-2"
          aria-label={`Call ${formatPhoneForDialing(phone)}`}
        >
          {loading === 'call' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Phone className="h-4 w-4 text-emerald-600" />
          )}
          <span>Call</span>
        </Button>
      )}

      {hasEmail && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleEmail}
          disabled={loading === 'email'}
          className="h-11 px-4 gap-2"
          aria-label={`Email ${email}`}
        >
          {loading === 'email' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mail className="h-4 w-4 text-blue-600" />
          )}
          <span>Email</span>
        </Button>
      )}

      {hasAddress && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDirections}
          disabled={loading === 'directions'}
          className="h-11 px-4 gap-2"
          aria-label={`Get directions to ${businessName || address}`}
        >
          {loading === 'directions' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4 text-purple-600" />
          )}
          <span>Directions</span>
        </Button>
      )}
    </div>
  );
};

/**
 * Clickable phone number with call and copy options
 */
export const ClickablePhone = ({ 
  phone, 
  extension,
  className = '',
  showIcon = true,
  showLabel = true,
}) => {
  const [loading, setLoading] = useState(false);

  if (!phone || !isValidPhone(phone)) {
    return phone ? <span className={className}>{phone}</span> : null;
  }

  const displayPhone = formatPhoneForDisplay(phone);

  const handleCall = async (e) => {
    e.stopPropagation();
    setLoading(true);
    await openPhoneDialer(phone, extension);
    setLoading(false);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    await copyToClipboard(formatPhoneForDialing(phone));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 rounded ${className}`}
          aria-label={`Phone options for ${displayPhone}`}
          data-testid="clickable-phone"
        >
          {showIcon && <Phone className="h-3.5 w-3.5" />}
          <span>{displayPhone}</span>
          {extension && <span className="text-slate-500">ext. {extension}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={handleCall} disabled={loading}>
          <Phone className="h-4 w-4 mr-2 text-emerald-600" />
          {loading ? 'Opening...' : 'Call'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopy}>
          <Copy className="h-4 w-4 mr-2 text-slate-500" />
          Copy Number
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Clickable email address with compose and copy options
 */
export const ClickableEmail = ({ 
  email, 
  subject = '',
  className = '',
  showIcon = true,
}) => {
  const [loading, setLoading] = useState(false);

  if (!email || !isValidEmail(email)) {
    return email ? <span className={className}>{email}</span> : null;
  }

  const handleEmail = async (e) => {
    e.stopPropagation();
    setLoading(true);
    await openEmailComposer(email, subject);
    setLoading(false);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    await copyToClipboard(email);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded ${className}`}
          aria-label={`Email options for ${email}`}
          data-testid="clickable-email"
        >
          {showIcon && <Mail className="h-3.5 w-3.5" />}
          <span className="truncate max-w-[200px]">{email}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={handleEmail} disabled={loading}>
          <Mail className="h-4 w-4 mr-2 text-blue-600" />
          {loading ? 'Opening...' : 'Send Email'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopy}>
          <Copy className="h-4 w-4 mr-2 text-slate-500" />
          Copy Email
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Clickable address with directions and copy options
 */
export const ClickableAddress = ({ 
  address, 
  businessName = '',
  className = '',
  showIcon = true,
}) => {
  const [loading, setLoading] = useState(false);
  const addressString = typeof address === 'string' ? address : buildAddressString(address);

  if (!addressString && !businessName) {
    return null;
  }

  const displayText = addressString || businessName;

  const handleDirections = async (e) => {
    e.stopPropagation();
    setLoading(true);
    await openMapsWithDirections(address, businessName);
    setLoading(false);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    const textToCopy = addressString || businessName;
    await copyToClipboard(textToCopy);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`inline-flex items-start gap-1.5 text-purple-600 hover:text-purple-700 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 rounded text-left ${className}`}
          aria-label={`Address options for ${displayText}`}
          data-testid="clickable-address"
        >
          {showIcon && <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
          <span>{displayText}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={handleDirections} disabled={loading}>
          <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
          {loading ? 'Opening...' : 'Get Directions'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopy}>
          <Copy className="h-4 w-4 mr-2 text-slate-500" />
          Copy Address
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Add to Calendar button for date records
 * Uses native calendar sync on mobile, ICS export on web
 */
export const AddToCalendarButton = ({
  dateRecord,
  variant = 'outline',
  size = 'sm',
  className = '',
  onSyncComplete, // Callback to update parent with new event ID
}) => {
  const [loading, setLoading] = useState(false);
  const isSynced = !!dateRecord?.native_calendar_event_id;

  const handleAddToCalendar = async () => {
    setLoading(true);
    try {
      // Import native calendar service
      const nativeCalendarService = await import('@/services/nativeCalendarService');
      
      if (nativeCalendarService.isNativeCalendarAvailable()) {
        // Native platform - use true calendar sync
        const result = await nativeCalendarService.syncDateToCalendar(
          dateRecord,
          dateRecord.native_calendar_event_id // Pass existing ID if re-syncing
        );
        
        if (result.success) {
          toast.success(
            result.action === 'updated' 
              ? 'Calendar event updated' 
              : 'Added to your calendar',
            { description: dateRecord.title || 'Event synced' }
          );
          
          // Notify parent to persist the event ID
          if (onSyncComplete && result.eventId) {
            onSyncComplete({
              dateId: dateRecord.id,
              eventId: result.eventId,
              status: 'synced'
            });
          }
        } else {
          toast.error('Failed to sync to calendar', {
            description: result.error || 'Please try again'
          });
        }
      } else {
        // Web fallback - download ICS file
        const nativeIntegrations = await import('@/services/nativeIntegrationsService');
        const { calendarService } = nativeIntegrations;
        const eventData = calendarService.createEventFromDate(dateRecord);
        await calendarService.addEvent(eventData);
      }
    } catch (error) {
      console.error('Failed to add to calendar:', error);
      toast.error('Failed to add event to calendar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleAddToCalendar}
      disabled={loading}
      className={`gap-2 ${className}`}
      aria-label={isSynced ? "Update calendar event" : "Add to phone calendar"}
      data-testid={`add-to-calendar-btn-${dateRecord?.id || 'unknown'}`}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isSynced ? (
        <CalendarCheck className="h-4 w-4 text-green-600" />
      ) : (
        <ExternalLink className="h-4 w-4" />
      )}
      <span>{isSynced ? 'Synced' : 'Add to Calendar'}</span>
    </Button>
  );
};

export default {
  ContactActionRow,
  ClickablePhone,
  ClickableEmail,
  ClickableAddress,
  AddToCalendarButton,
};
